// @ts-check
import React from 'react';
import ArrowLeftIcon from '@liquidweb-dev/undercurrent/icons/lucide/ArrowLeft';
import CloseIcon from '@liquidweb-dev/undercurrent/icons/lucide/X';
import Dialog from '@liquidweb-dev/undercurrent/Dialog';
import DialogContent from '@liquidweb-dev/undercurrent/DialogContent';
import IconButton from '@liquidweb-dev/undercurrent/IconButton';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';
import { CANCELLATION_STAGE } from './constants';

/**
 * @typedef DialogHeaderProps
 * @property {boolean} [showPrevious]
 * @property {() => void} [onPrevious]
 * @property {() => void} onClose
 */

/**
 * @param {DialogHeaderProps} props
 */
const DialogHeader = ({ showPrevious, onPrevious, onClose }) => (
	<Stack
		direction="row"
		alignItems="center"
		justifyContent={showPrevious ? 'space-between' : 'flex-end'}
		margin={300}
	>
		{showPrevious && (
			<IconButton
				variant="barebones"
				size="large"
				onClick={onPrevious}
				aria-label="Go back"
			>
				<ArrowLeftIcon />
			</IconButton>
		)}
		<IconButton
			variant="barebones"
			size="large"
			onClick={onClose}
			aria-label="Close dialog"
		>
			<CloseIcon />
		</IconButton>
	</Stack>
);

/**
 * @typedef WrapperProps
 * @property {React.ReactNode} children
 * @property {string} [currentStage]
 * @property {() => void} [onPrevious]
 * @property {() => void} onClose
 */

/**
 * @param {WrapperProps & Omit<import('@liquidweb-dev/undercurrent/Dialog').DialogProps, 'onClose'>} props
 */
const Wrapper = ({
	children,
	currentStage,
	onPrevious,
	onClose,
	sx,
	...dialogProps
}) => {
	const showPrevious = Boolean(
		currentStage &&
			currentStage !== CANCELLATION_STAGE.CANCEL_SERVICE &&
			currentStage !== CANCELLATION_STAGE.SUBMITTED_REQUEST,
	);
	return (
		<Dialog maxWidth="md" fullWidth onClose={onClose} {...dialogProps}>
			<DialogHeader
				showPrevious={showPrevious}
				onPrevious={onPrevious}
				onClose={onClose}
			/>
			<DialogContent
				sx={combineSx(
					{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						paddingX: { xs: 400, sm: 700 },
						paddingTop: 100,
						paddingBottom: 500,
					},
					sx,
				)}
			>
				{children}
			</DialogContent>
		</Dialog>
	);
};

export default Wrapper;
